/**
 * Auth Context
 *
 * Identity comes from Clerk; the MyLeadSyncer account (plan, admin flag,
 * workspace) comes from GET /api/users/me once Clerk reports a signed-in
 * session. apiRequest() is wired to Clerk's session token from here.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth as useClerkAuth, useClerk } from '@clerk/react';
import { authApi, setTokenGetter, setUnauthorizedHandler, setWorkspaceId } from './api';
import type { User } from './api';

/** Why a signed-in Clerk session has no usable MyLeadSyncer account. */
export type AuthIssue = 'email_unverified' | 'load_failed' | null;

interface AuthContextType {
  user: User | null;
  /** True until Clerk has loaded and (when signed in) /api/users/me has answered. */
  loading: boolean;
  /** Clerk session state, independent of the /me load. */
  isSignedIn: boolean;
  isAuthenticated: boolean;
  issue: AuthIssue;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, userId, getToken } = useClerkAuth();
  const clerk = useClerk();
  const [user, setUser] = useState<User | null>(null);
  /** The Clerk userId whose /me load has finished (success or not). */
  const [meFor, setMeFor] = useState<string | null>(null);
  const [issue, setIssue] = useState<AuthIssue>(null);

  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const logout = useCallback(async () => {
    setWorkspaceId(null);
    setUser(null);
    try {
      await clerk.signOut({ redirectUrl: '/login' });
    } catch (err) {
      console.error('Logout error:', err);
      window.location.assign('/login');
    }
  }, [clerk]);

  // Must be declared before the /me effect: effects run in order, so the
  // token getter is in place before the first API call.
  useEffect(() => {
    setTokenGetter((opts) => getTokenRef.current(opts));
    setUnauthorizedHandler(() => { void logout(); });
    return () => {
      setTokenGetter(null);
      setUnauthorizedHandler(null);
    };
  }, [logout]);

  const loadMe = useCallback(async () => {
    const response = await authApi.getMe();
    if (response.success && response.data) {
      setUser(response.data);
      setIssue(null);
    } else {
      setUser(null);
      setIssue(response.status === 403 && response.error === 'email_unverified' ? 'email_unverified' : 'load_failed');
    }
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn || !userId) {
      setUser(null);
      setIssue(null);
      setMeFor(null);
      return;
    }
    let cancelled = false;
    loadMe().finally(() => { if (!cancelled) setMeFor(userId); });
    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, userId, loadMe]);

  const refreshUser = useCallback(async () => {
    if (!isSignedIn) return;
    await loadMe();
  }, [isSignedIn, loadMe]);

  const value: AuthContextType = {
    user,
    loading: !isLoaded || (!!isSignedIn && meFor !== userId),
    isSignedIn: !!isSignedIn,
    isAuthenticated: user !== null,
    issue,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
